import { useMemo } from "react";
import { Stack } from "@mui/material";
import { MobileTransactionCard } from "./MobileTransactionCard";
import { buildAccountNumberIndex } from "../../logic/counterparty";
import { useRegistry } from "../../context/RegistryContext";
import type { EditSession } from "./editSession";
import type {
  AutoTransactionRuleDto,
  CurrencyFormat,
  TransactionId,
  TransactionListDto,
} from "../../api/types";

export type TransactionListMobileProps = {
  transactions: TransactionListDto[];
  session: EditSession;
  matchingRulesMap: Record<TransactionId, AutoTransactionRuleDto[]>;
  currencyFormat: CurrencyFormat;
  onViewDetails: (id: TransactionId) => void;
  onCreateRule: (tx: TransactionListDto) => void;
  onOpenSuggestions: (
    event: React.MouseEvent<HTMLElement>,
    txId: TransactionId,
  ) => void;
  onOpenAppliedRules: (
    event: React.MouseEvent<HTMLElement>,
    tx: TransactionListDto,
  ) => void;
};

export const TransactionListMobile = ({
  transactions,
  session,
  matchingRulesMap,
  currencyFormat,
  onViewDetails,
  onCreateRule,
  onOpenSuggestions,
  onOpenAppliedRules,
}: TransactionListMobileProps) => {
  // Hidden accounts included: a transfer to one still names it.
  const { sources } = useRegistry().withHidden;

  // One index for the whole list: the cards are memoized, so building it per
  // card would hand each one a fresh Map and defeat that.
  const accountNumberIndex = useMemo(
    () => buildAccountNumberIndex(sources),
    [sources],
  );

  return (
    <Stack spacing={1}>
      {transactions.map((tx) => (
        <MobileTransactionCard
          key={tx.id}
          transaction={tx}
          session={session}
          matchingRules={matchingRulesMap[tx.id]}
          currencyFormat={currencyFormat}
          accountNumberIndex={accountNumberIndex}
          onViewDetails={onViewDetails}
          onCreateRule={onCreateRule}
          onOpenSuggestions={onOpenSuggestions}
          onOpenAppliedRules={onOpenAppliedRules}
        />
      ))}
    </Stack>
  );
};
